import React, {useEffect, useMemo, useState} from "react";
import {Alert, Button, Paper, Stack, Typography} from "@mui/material";
import {getBackendErrorMessage} from "../../../shared/utils/httpError";
import {listRecursosDoSistema} from "../PlanosComerciaisService";
import {
    getRecursosRecusados,
    idsNoDireitoDoPlano,
    normalizeModuloOptions,
    normalizePerfisModelo,
    perfisModeloParaEnvio
} from "../planosComerciaisUtils";
import {formDosPerfis} from "../planoVersaoUtils";
import PerfisModeloEditor from "./PerfisModeloEditor";

/*
 * Perfis de acesso que toda assinatura nova do plano recebe (operador por tenant). São do plano, não da versão: o
 * direito que os limita são os módulos incluídos da versão que vale (a à venda). Salvam pelo PUT do plano.
 */
export default function PerfisDoPlanoCard({plano, versaoQueVale, modulos, onSalvar}) {
    const [form, setForm] = useState(() => formDosPerfis(plano, versaoQueVale, normalizePerfisModelo(plano.perfis_modelo)));
    const [recursos, setRecursos] = useState({status: 'carregando', itens: []});
    const [salvando, setSalvando] = useState(false);
    const [recusados, setRecusados] = useState(null);
    const [mensagem, setMensagem] = useState({erro: '', ok: ''});

    useEffect(() => {
        setForm(formDosPerfis(plano, versaoQueVale, normalizePerfisModelo(plano.perfis_modelo)));
        setRecusados(null);
    }, [plano, versaoQueVale]);

    useEffect(() => {
        let ativo = true;
        setRecursos({status: 'carregando', itens: []});
        listRecursosDoSistema(plano.id_sistema)
            .then((response) => ativo && setRecursos({status: 'pronto', itens: response.data.items}))
            .catch(() => ativo && setRecursos({status: 'erro', itens: []}));
        return () => {
            ativo = false;
        };
    }, [plano.id_sistema]);

    const opcoesDeModulo = useMemo(() => normalizeModuloOptions(modulos), [modulos]);
    const recursosPorSistema = useMemo(() => ({[plano.id_sistema]: recursos}), [plano.id_sistema, recursos]);

    const corpo = perfisModeloParaEnvio(form.perfis_modelo, {
        original: normalizePerfisModelo(plano.perfis_modelo),
        recursosNoDireito: recursos.status === 'pronto' ? idsNoDireitoDoPlano(form, recursos.itens) : null,
    });

    const salvar = async () => {
        if (!corpo) return;
        setSalvando(true);
        setMensagem({erro: '', ok: ''});
        setRecusados(null);
        try {
            await onSalvar({perfis_modelo: corpo});
            setMensagem({erro: '', ok: 'Perfis salvos. Valem para as próximas assinaturas do plano.'});
        } catch (error) {
            const recusa = getRecursosRecusados(error, form.perfis_modelo);
            setRecusados(recusa);
            setMensagem({erro: recusa
                ? 'A API recusou recursos fora do direito do plano. Veja os destacados.'
                : getBackendErrorMessage(error, 'Não foi possível salvar os perfis.'), ok: ''});
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{p: {xs: 2, md: 2.5}}} component="section" aria-label="Perfis de acesso da assinatura">
            <PerfisModeloEditor form={form} modulos={opcoesDeModulo} recursosPorSistema={recursosPorSistema} perfisRecusados={recusados} onChange={setForm} />
            <Stack spacing={1} sx={{mt: 2}}>
                <Typography variant="body2" color="text.secondary">
                    Os perfis são do plano e valem para toda assinatura nova, em qualquer versão. O direito que os limita é o da versão à venda.
                </Typography>
                {mensagem.erro ? <Alert severity="error">{mensagem.erro}</Alert> : null}
                {mensagem.ok ? <Alert severity="success">{mensagem.ok}</Alert> : null}
                <Stack direction="row" justifyContent="flex-end">
                    <Button variant="contained" disabled={!corpo || salvando} onClick={salvar}>{salvando ? 'Salvando…' : 'Salvar os perfis'}</Button>
                </Stack>
            </Stack>
        </Paper>
    );
}
